
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePageContext } from '@/hooks/usePageContext';
import { useUserDashboardData } from '@/hooks/useUserDashboardData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { FileText, Calendar, Settings, Users, Eye, ArrowRight, Clock, LayoutDashboard, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

const Dashboard: React.FC = () => {
  const { user: authUser } = useAuth();
  const { direction } = useLanguage();
  const { user, userRole, settings, times, isLoading: isLoadingUserData } = useUserDashboardData();
  
  // Safely access page context with fallback
  let pageContext;
  try {
    pageContext = usePageContext();
  } catch (error) {
    console.error('PageContext not available:', error);
    // Return loading state if context isn't available
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-synagogue-blue"></div>
      </div>
    );
  }
  
  const { pages } = pageContext;
  const [currentTime, setCurrentTime] = useState<string>('');
  const [greeting, setGreeting] = useState<string>('');
  const isRtl = direction === 'rtl';
  
  // Log all loaded data for debugging purposes
  useEffect(() => {
    console.log('Dashboard data:', {
      authUser,
      userData: user,
      settings,
      times,
      pages,
      userRole
    });
  }, [authUser, user, settings, times, pages, userRole]);
  
  // Stats
  const totalPages = pages?.length || 0;
  const activePages = pages?.filter(page => page.enabled)?.length || 0;
  const inactivePages = totalPages - activePages;
  const totalPrayerTimes = times?.length || 0;
  
  // Update time every second and set greeting
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString(isRtl ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
      setCurrentTime(timeStr);
      
      const hour = now.getHours();
      if (hour >= 5 && hour < 12) {
        setGreeting(isRtl ? 'בוקר טוב' : 'Good morning');
      } else if (hour >= 12 && hour < 18) {
        setGreeting(isRtl ? 'צהריים טובים' : 'Good afternoon');
      } else {
        setGreeting(isRtl ? 'ערב טוב' : 'Good evening');
      }
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000);
    
    return () => clearInterval(interval);
  }, [isRtl]);

  // Show loading state
  if (isLoadingUserData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-synagogue-blue"></div>
        <p className="ml-4 text-lg">{isRtl ? 'טוען נתוני משתמש...' : 'Loading user data...'}</p>
      </div>
    );
  }

  // Get the username from either user data or auth user
  const username = user?.email?.split('@')[0] || authUser?.email?.split('@')[0] || '';

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-synagogue-blue to-blue-600 text-white border-none">
        <CardContent className="p-6">
          <div className={`flex ${isRtl ? 'flex-row-reverse' : ''} justify-between items-center`}>
            <div>
              <h1 className="text-3xl font-bold">{greeting}, {username}</h1>
              <p className="text-blue-100 mt-1">{isRtl ? 'ברוך הבא ללוח הבקרה' : 'Welcome to your dashboard'}</p>
              {user?.role && (
                <p className="text-blue-200 mt-1">
                  {isRtl ? `תפקיד: ${user.role}` : `Role: ${user.role}`}
                </p>
              )}
            </div>
            <div className="text-3xl font-bold">{currentTime}</div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card className="bg-white border-blue-100 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-synagogue-blue">{isRtl ? 'סך הכל דפים' : 'Total Pages'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{totalPages}</div>
              <div className="p-2 bg-blue-50 rounded-full">
                <FileText className="h-6 w-6 text-synagogue-blue" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-0 pb-3">
            <Button variant="link" size="sm" asChild className={`p-0 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/pages">
                {isRtl ? 'ניהול דפים' : 'Manage Pages'}
                {isRtl ? <ArrowRight className="ml-1 h-4 w-4" /> : <ArrowRight className="mr-1 h-4 w-4" />}
              </Link>
            </Button>
          </CardFooter>
        </Card>

        <Card className="bg-white border-green-100 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-green-600">{isRtl ? 'דפים פעילים' : 'Active Pages'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{activePages}</div>
              <div className="p-2 bg-green-50 rounded-full">
                <Monitor className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-0 pb-3">
            <p className="text-sm text-muted-foreground">
              {isRtl 
                ? `${Math.round((activePages / Math.max(totalPages, 1)) * 100)}% מהדפים פעילים` 
                : `${Math.round((activePages / Math.max(totalPages, 1)) * 100)}% of pages active`}
            </p>
          </CardFooter>
        </Card>

        <Card className="bg-white border-orange-100 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-orange-600">{isRtl ? 'זמני תפילה' : 'Prayer Times'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{totalPrayerTimes}</div>
              <div className="p-2 bg-orange-50 rounded-full">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-0 pb-3">
            <Button variant="outline" size="sm" asChild className={`${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/times">
                <Clock className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'נהל זמנים' : 'Manage Times'}
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* Settings Summary */}
      {settings && Object.keys(settings).length > 0 && (
        <Card className="bg-white border-purple-100 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg text-purple-700">{isRtl ? 'הגדרות משתמש' : 'User Settings'}</CardTitle>
            <CardDescription>{isRtl ? 'סקירת הגדרותיך' : 'Overview of your settings'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {settings.location && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h3 className="font-medium text-purple-800">{isRtl ? 'מיקום' : 'Location'}</h3>
                  <p className="text-sm mt-1">{settings.location.name}</p>
                </div>
              )}
              {settings.rotationInterval !== undefined && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h3 className="font-medium text-purple-800">{isRtl ? 'מרווח סיבוב' : 'Rotation Interval'}</h3>
                  <p className="text-sm mt-1">{settings.rotationInterval} {isRtl ? 'שניות' : 'seconds'}</p>
                </div>
              )}
              {settings.autoScroll !== undefined && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h3 className="font-medium text-purple-800">{isRtl ? 'גלילה אוטומטית' : 'Auto Scroll'}</h3>
                  <p className="text-sm mt-1">{settings.autoScroll ? (isRtl ? 'מופעל' : 'Enabled') : (isRtl ? 'כבוי' : 'Disabled')}</p>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" asChild className={`${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/settings">
                <Settings className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'ערוך הגדרות' : 'Edit Settings'}
              </Link>
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Prayer Times Summary */}
      {times && times.length > 0 && (
        <Card className="bg-white border-amber-100 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg text-amber-700">{isRtl ? 'זמני תפילה שלך' : 'Your Prayer Times'}</CardTitle>
            <CardDescription>
              {isRtl 
                ? `${times.length} זמני תפילה מוגדרים` 
                : `${times.length} prayer times configured`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {times.slice(0, 3).map(time => (
                <div key={time.id} className="flex justify-between items-center p-2 rounded-lg bg-amber-50">
                  <span className="font-medium">{time.title}</span>
                  <span className="text-amber-700">{time.time}</span>
                </div>
              ))}
              {times.length > 3 && (
                <div className="text-center text-sm text-muted-foreground mt-2">
                  {isRtl 
                    ? `ועוד ${times.length - 3} נוספים...` 
                    : `And ${times.length - 3} more...`}
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button variant="outline" size="sm" asChild className={`${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/times">
                <Clock className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'ערוך זמני תפילה' : 'Manage Prayer Times'}
              </Link>
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Quick Access Cards */}
      <h2 className={`text-xl font-bold ${isRtl ? 'text-right' : 'text-left'} text-synagogue-blue`}>
        {isRtl ? 'גישה מהירה' : 'Quick Access'}
      </h2>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100 hover:shadow-md transition-all">
          <CardHeader className={`flex flex-row ${isRtl ? 'flex-row-reverse' : ''} items-center justify-between pb-2`}>
            <CardTitle className="text-sm font-medium text-purple-700">{isRtl ? 'ניהול תצוגה' : 'Display Management'}</CardTitle>
            <div className="p-1 bg-purple-100 rounded-full">
              <FileText className="h-4 w-4 text-purple-700" />
            </div>
          </CardHeader>
          <CardContent className={isRtl ? 'text-right' : 'text-left'}>
            <p className="text-sm text-gray-600 mt-2 mb-4">
              {isRtl ? 'צור וערוך דפים, הגדר סדר ואפשר או השבת דפים' : 'Create and edit pages, set order and enable or disable pages'}
            </p>
            <Button asChild variant="outline" className={`mt-4 w-full border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/pages">
                <LayoutDashboard className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'ניהול דפים' : 'Manage Pages'}
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100 hover:shadow-md transition-all">
          <CardHeader className={`flex flex-row ${isRtl ? 'flex-row-reverse' : ''} items-center justify-between pb-2`}>
            <CardTitle className="text-sm font-medium text-amber-700">{isRtl ? 'ניהול יארצייט' : 'Yahrzeit Management'}</CardTitle>
            <div className="p-1 bg-amber-100 rounded-full">
              <Calendar className="h-4 w-4 text-amber-700" />
            </div>
          </CardHeader>
          <CardContent className={isRtl ? 'text-right' : 'text-left'}>
            <p className="text-sm text-gray-600 mt-2 mb-4">
              {isRtl ? 'נהל רשומות יארצייט לתצוגה אוטומטית על הלוח' : 'Manage yahrzeit records for automatic display on the board'}
            </p>
            <Button asChild variant="outline" className={`mt-4 w-full border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/yahrzeit">
                <Calendar className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'ניהול יארצייט' : 'Manage Yahrzeit'}
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-100 hover:shadow-md transition-all">
          <CardHeader className={`flex flex-row ${isRtl ? 'flex-row-reverse' : ''} items-center justify-between pb-2`}>
            <CardTitle className="text-sm font-medium text-teal-700">{isRtl ? 'הגדרות מערכת' : 'System Settings'}</CardTitle>
            <div className="p-1 bg-teal-100 rounded-full">
              <Settings className="h-4 w-4 text-teal-700" />
            </div>
          </CardHeader>
          <CardContent className={isRtl ? 'text-right' : 'text-left'}>
            <p className="text-sm text-gray-600 mt-2 mb-4">
              {isRtl ? 'התאם את הגדרות המערכת, זמני תצוגה ומאפיינים נוספים' : 'Adjust system configuration, display times and additional features'}
            </p>
            <Button asChild variant="outline" className={`mt-4 w-full border-teal-200 text-teal-700 hover:bg-teal-50 hover:text-teal-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link to="/dashboard/settings">
                <Settings className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'הגדרות מערכת' : 'System Settings'}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Display Board Button */}
      <Card className="bg-gradient-to-r from-blue-600 to-synagogue-blue hover:from-blue-700 hover:to-blue-900 text-white border-none hover:shadow-lg transition-all">
        <CardContent className="p-6">
          <div className={`flex ${isRtl ? 'flex-row-reverse' : ''} justify-between items-center`}>
            <div>
              <h2 className="text-xl font-bold">{isRtl ? 'צפייה בלוח התצוגה' : 'View Display Board'}</h2>
              <p className="text-blue-100 mt-1">{isRtl ? 'צפייה בלוח במצב מסך מלא' : 'View the board in fullscreen mode'}</p>
            </div>
            <Button asChild variant="secondary" className="bg-white text-synagogue-blue hover:bg-blue-50">
              <Link to="/display">
                <Eye className={`${isRtl ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                {isRtl ? 'צפה עכשיו' : 'View Now'}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
